<script>
/* global axios */
import { useAlert } from 'dashboard/composables';
import FlowConfigurationPage from './FlowConfigurationPage.vue';

/* Configuração de IA das caixas de WhatsApp.
   Várias IAs por caixa (ex.: uma recepcionista + uma por banco), cada uma com
   personalidade, regras, proibições, segurança, FAQ e ferramentas próprias.
   A configuração NÃO fica em additional_attributes (é lida por qualquer
   atendente pela API de caixas) — mora no InstallationConfig, via MazamInboxIa. */

const MAX_AGENTS = 8;
const TIPOS_PARAM = [
  { v: 'texto', t: 'Texto' }, { v: 'numero', t: 'Número' }, { v: 'valor', t: 'Valor (R$)' },
  { v: 'cpf', t: 'CPF' }, { v: 'cnpj', t: 'CNPJ' }, { v: 'telefone', t: 'Telefone' },
  { v: 'email', t: 'E-mail' }, { v: 'data', t: 'Data' }, { v: 'booleano', t: 'Sim/Não' },
  { v: 'opcao', t: 'Lista de opções' },
];

/* Modelos prontos: página em branco paralisa quem não é programador. */
const MODELOS = {
  recepcao: {
    nome: 'Júlia — Recepção',
    persona: 'Você é a Júlia, atendente da Mazam. Fala como brasileiro no WhatsApp: '
      + 'frases curtas, simpática e direta, sem termos técnicos e sem exagero de emoji. '
      + 'Trata o cliente por você.',
    regras: ['Descubra o que o cliente precisa antes de qualquer coisa.',
      'Se for sobre FGTS ou consignado CLT, transfira para o especialista.',
      'Uma pergunta por vez.'],
    proibicoes: ['Nunca prometa valor, taxa ou aprovação.',
      'Nunca peça senha do Gov.br, do banco ou código de SMS.'],
  },
  banco: {
    nome: 'Rafa — FGTS',
    persona: 'Você é o Rafa, especialista em antecipação de FGTS da Mazam. '
      + 'Objetivo, cordial e claro. Explica em palavras simples.',
    regras: ['Confirme o CPF antes de simular.',
      'Só informe valores que vieram da consulta.',
      'Se o cliente não tem saldo, explique com gentileza e ofereça avisar depois.'],
    proibicoes: ['Nunca prometa aprovação ou prazo de pagamento.',
      'Nunca cite taxa que não veio da simulação.',
      'Nunca fale de outro produto que não seja o seu.'],
  },
  duvidas: {
    nome: 'Tira-dúvidas',
    persona: 'Você responde dúvidas gerais sobre os produtos da Mazam, '
      + 'de forma curta e honesta.',
    regras: ['Responda só o que estiver nas perguntas frequentes.',
      'Se não souber, diga que vai chamar um atendente.'],
    proibicoes: ['Nunca invente informação.', 'Nunca fale valores.'],
  },
};

export default {
  components: { FlowConfigurationPage },
  props: {
    inbox: { type: Object, required: true },
    // 'whatsapp' ou 'webchat'. A tela é a MESMA; muda só a explicação e o
    // aviso sobre o bot da caixa (que no webchat também existe).
    canal: { type: String, default: 'whatsapp' },
    // Controle de Acesso: sem a key 'Inbox Fluxo Webchat Editar' a tela abre
    // para CONSULTA (nenhum botão de salvar, campos desabilitados). Default
    // false = comportamento atual — a aba "Fluxo IA" do WhatsApp fica intacta.
    somenteLeitura: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      saving: false,
      enabled: false,
      defaultAgent: '',
      agents: [],
      tools: [],
      agentBot: null,
      selId: null,
      aba: 'ia',            // 'ia' | 'fluxo'
      toolAberta: null,
      tipos: TIPOS_PARAM,
      iaModelo: 'gpt-5-mini',
      iaChave: '',
      iaTemChave: false,
      iaDica: '',
      iaSalvando: false,
      usarChaveGeral: true,   // padrão: aproveita a chave da conta
      openaiGeralDica: '',
      chaveOrigem: '',
      modelos: ['gpt-5-mini', 'gpt-5', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini'],
      // Segredos dos nós de ação (HTTP/script): nome + dica mascarada + valor
      // novo (digitado agora). O valor cru NUNCA volta do servidor.
      segredos: [],          // [{nome, dica, valor}]
      supabaseTabelas: [],   // allowlist de tabelas do nó Supabase
      tabelaNova: '',
    };
  },
  computed: {
    accountId() { return this.$route.params.accountId; },
    ehWhats() { return this.canal === 'whatsapp'; },
    titulo() { return this.ehWhats ? 'Fluxo de atendimento IA' : 'Fluxo de atendimento'; },
    explicacao() {
      return this.ehWhats
        ? 'Monte o atendimento desta caixa: crie uma ou mais IAs (por exemplo, uma '
          + 'recepcionista e uma para cada banco) e o roteiro com botões, listas e '
          + 'carrossel do WhatsApp.'
        : 'Monte o atendimento da sua página: crie uma ou mais IAs e o roteiro com '
          + 'botões, listas e carrossel. Aqui os blocos são desenhados pelo nosso '
          + 'próprio site — não dependem do WhatsApp.';
    },
    sel() { return this.agents.find(a => a.id === this.selId) || null; },
    // O roteiro vive dentro do construtor embutido; olhamos por ele.
    fluxoNos() { return (this.$refs.flow && this.$refs.flow.nodes) || []; },
    temRoteiro() { return this.fluxoNos.length > 0 && this.roteiroAtivo; },
    usaIa() { return this.temRoteiro && this.fluxoNos.some(n => n.type === 'ai'); },
    roteiroAtivo() { return !!(this.$refs.flow && this.$refs.flow.enabled); },
    ativos() { return this.agents.filter(a => a.ativo); },
    problemas() {
      const out = [];
      // Roteiro com botões funciona SOZINHO, sem IA nenhuma. Só cobramos uma IA
      // quando o roteiro manda "Passar para IA" ou quando não há roteiro (aí a
      // caixa não teria ninguém atendendo).
      if (this.enabled && !this.roteiroAtivo && !this.agents.length) {
        out.push('O atendimento está LIGADO, mas o roteiro está desativado e não há '
          + 'nenhuma IA — ninguém vai responder. Ligue o roteiro na aba "Roteiro" '
          + 'ou crie uma IA.');
      } else if (!this.agents.length && this.usaIa) {
        out.push('O roteiro tem um bloco "Passar para IA", mas nenhuma IA foi criada.');
      }
      if (this.agents.length && !this.agents.some(a => a.id === this.defaultAgent && a.ativo)) {
        out.push('Escolha qual IA atende primeiro (e ela precisa estar ligada).');
      }
      this.agents.forEach(a => {
        if (!a.nome) out.push(`Uma das IAs está sem nome.`);
        if (!a.persona) out.push(`"${a.nome || a.id}" está sem personalidade.`);
      });
      this.tools.forEach(t => {
        if (!t.url) out.push(`Ferramenta "${t.nome}" está sem endereço.`);
        if (!t.descricao) out.push(`Ferramenta "${t.nome}" está sem explicação do que faz.`);
      });
      return out;
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/ia`,
          { params: { inbox_id: this.inbox.id } }
        );
        const c = data.config || {};
        this.enabled = c.enabled === true;
        this.defaultAgent = c.default_agent || '';
        this.agents = (c.agents || []).map(a => this.normAgent(a));
        this.tools = (c.tools || []).map(t => this.normTool(t));
        this.agentBot = data.agent_bot || null;
        this.iaModelo = data.modelo || 'gpt-5-mini';
        this.iaTemChave = !!data.tem_chave;
        this.iaDica = data.chave_dica || '';
        // Mesma regra do servidor: sem escolha gravada, caixa com chave
        // própria continua com ela; sem chave, usa a geral.
        this.usarChaveGeral = c.usar_chave_geral === undefined
          ? !data.tem_chave
          : c.usar_chave_geral !== false;
        this.openaiGeralDica = data.openai_geral_dica || '';
        this.chaveOrigem = data.chave_origem || '';
        // Segredos: só nomes + dica; valor novo começa vazio (digitar = trocar).
        const dicas = data.segredos_dicas || {};
        this.segredos = (data.segredos_nomes || []).map(n => ({ nome: n, dica: dicas[n] || '', valor: '' }));
        this.supabaseTabelas = Array.isArray(c.supabase_tabelas) ? c.supabase_tabelas.slice() : [];
        this.selId = this.agents[0] ? this.agents[0].id : null;
      } catch (e) {
        useAlert('Não consegui carregar a configuração.');
      }
      this.loading = false;
    },
    // Só manda segredo com valor digitado agora (vazio = mantém o gravado).
    segredosNovos() {
      const out = {};
      this.segredos.forEach(s => {
        const nome = String(s.nome || '').trim().replace(/[^a-zA-Z0-9_]/g, '');
        if (nome && String(s.valor || '').length) out[nome] = s.valor;
      });
      return out;
    },
    addSegredo() { this.segredos.push({ nome: '', dica: '', valor: '' }); },
    delSegredo(i) { this.segredos.splice(i, 1); },
    addTabela() {
      const t = String(this.tabelaNova || '').trim().replace(/[^a-zA-Z0-9_]/g, '');
      if (t && !this.supabaseTabelas.includes(t)) this.supabaseTabelas.push(t);
      this.tabelaNova = '';
    },
    delTabela(i) { this.supabaseTabelas.splice(i, 1); },
    normAgent(a) {
      return {
        id: a.id, nome: '', ativo: true, persona: '', regras: [], proibicoes: [],
        guardrail: '', frases_proibidas: [], faq: [], tools: [],
        pode_transferir_para: [], max_chars_resposta: 500,
        guardrail_opcoes: {
          bloquear_promessa_aprovacao: true, bloquear_pii_na_saida: true,
          exigir_simulacao_real_para_oferta: true, somente_nosso_produto: true,
        },
        ...a,
      };
    },
    normTool(t) {
      return { nome: '', titulo: '', descricao: '', quando_usar: '', url: '', metodo: 'POST',
        parametros: [], timeout_seg: 10, mensagem_espera: '', resposta_campos: [], ...t };
    },
    novoId(base) {
      let i = 1;
      let id = base;
      while (this.agents.some(a => a.id === id)) { i += 1; id = `${base}${i}`; }
      return id;
    },
    /* Cria a IA. Sem argumento = EM BRANCO (nome e personalidade são seus).
       O id é NEUTRO (ia1, ia2…) de propósito: antes ele vinha do modelo, então
       uma IA de banco criada pelo modelo de recepção ficava com id `recepcao`
       para sempre — e é por esse id que as transferências e o bloco "Passar
       para IA" apontam. */
    addAgent(modelo) {
      if (this.agents.length >= MAX_AGENTS) return;
      const m = MODELOS[modelo] || null;
      const id = this.novoId('ia');
      const a = this.normAgent(
        m
          ? { id, nome: m.nome, persona: m.persona,
              regras: [...m.regras], proibicoes: [...m.proibicoes] }
          : { id, nome: '', persona: '' }
      );
      this.agents.push(a);
      if (!this.defaultAgent) this.defaultAgent = id;
      this.selId = id;
      this.aba = 'ia';
      // Foco no nome: a primeira coisa a fazer é batizar a IA.
      this.$nextTick(() => {
        const el = this.$refs.nomeIa;
        if (el && el.focus) el.focus();
      });
    },
    /* Preenche a IA ATUAL com um exemplo, sem trocar nome nem id — serve de
       ponto de partida para quem não quer encarar a tela em branco. */
    usarExemplo(modelo) {
      const m = MODELOS[modelo];
      if (!m || !this.sel) return;
      if (this.sel.persona && !window.confirm(
        'Isto substitui a personalidade e as regras desta IA pelo exemplo. Continuar?'
      )) return;
      this.sel.persona = m.persona;
      this.sel.regras = [...m.regras];
      this.sel.proibicoes = [...m.proibicoes];
      if (!this.sel.nome) this.sel.nome = m.nome;
    },
    removeAgent(id) {
      this.agents = this.agents.filter(a => a.id !== id);
      this.agents.forEach(a => {
        a.pode_transferir_para = (a.pode_transferir_para || []).filter(d => d !== id);
      });
      if (this.defaultAgent === id) this.defaultAgent = this.agents[0]?.id || '';
      if (this.selId === id) this.selId = this.agents[0]?.id || null;
    },
    addLinha(arr) { arr.push(''); },
    addFaq(a) { if (a.faq.length < 40) a.faq.push({ pergunta: '', resposta: '' }); },
    addTool() {
      if (this.tools.length >= 30) return;
      let i = this.tools.length + 1;
      while (this.tools.some(t => t.nome === `ferramenta_${i}`)) i += 1;
      const t = this.normTool({ nome: `ferramenta_${i}`, titulo: 'Nova ferramenta' });
      this.tools.push(t);
      this.toolAberta = t.nome;
    },
    addParam(t) {
      if (t.parametros.length >= 8) return;
      t.parametros.push({ nome: '', tipo: 'texto', titulo: '', descricao: '', obrigatorio: true });
    },
    toggleTool(a, nome) {
      const i = a.tools.indexOf(nome);
      if (i >= 0) a.tools.splice(i, 1);
      else if (a.tools.length < 12) a.tools.push(nome);
    },
    toggleTransfer(a, id) {
      const i = a.pode_transferir_para.indexOf(id);
      if (i >= 0) a.pode_transferir_para.splice(i, 1);
      else a.pode_transferir_para.push(id);
    },
    async salvarIa() {
      // Rede de segurança: mesmo com o botão escondido, nada de escrever sem
      // a key de edição (o backend também recusa com 403).
      if (this.somenteLeitura) return;
      this.iaSalvando = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/ia_credencial`,
          { inbox_id: this.inbox.id, modelo: this.iaModelo, chave: this.iaChave }
        );
        this.iaTemChave = !!data.tem_chave;
        this.iaDica = data.chave_dica || '';
        this.iaChave = '';
        useAlert('IA desta caixa salva.');
      } catch (e) {
        useAlert('Não foi possível salvar a IA.');
      }
      this.iaSalvando = false;
    },
    async save() {
      // Controle de Acesso: só leitura não salva (o evento @save vem do canvas
      // embutido, que também pode ser disparado por atalho).
      if (this.somenteLeitura) return;
      // E1: guard de reentrância — o botão de salvar dentro do canvas (em
      // tela cheia) não conhece o `saving` do pai, então dois cliques rápidos
      // (ou clique + Enter) disparavam dois POST /mazam_web/ia concorrentes.
      if (this.saving) return;
      // Exclusão mútua: com o roteiro ligado, quem atende é a IA daqui. O bot
      // da caixa precisa sair — confirmado com o usuário, nunca em silêncio.
      let desligarBot = false;
      if (this.enabled && this.agentBot) {
        const ok = window.confirm(
          `Esta caixa tem o bot "${this.agentBot.name}" ativo.\n\n`
          + 'Ligando a IA daqui, ela passa a atender e o bot é DESLIGADO desta caixa. '
          + 'Você pode religar depois em "Configuração do Bot".\n\nContinuar?'
        );
        if (!ok) return;
        desligarBot = true;
      }
      this.saving = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/ia`,
          {
            inbox_id: this.inbox.id,
            desligar_bot: desligarBot,
            config: {
              enabled: this.enabled,
              default_agent: this.defaultAgent,
              agents: this.agents,
              tools: this.tools,
              // Segredos dos nós (só os que o admin digitou agora; vazio = mantém)
              // e a allowlist de tabelas do Supabase.
              segredos: this.segredosNovos(),
              usar_chave_geral: this.usarChaveGeral,
              supabase_tabelas: this.supabaseTabelas,
              flow: this.$refs.flow ? this.$refs.flow.exportFlow() : undefined,
            },
          }
        );
        if (data.bot_desligado) this.agentBot = null;
        useAlert('Configuração salva.');
      } catch (e) {
        useAlert('Não foi possível salvar.');
      }
      this.saving = false;
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-5xl">
    <h3 class="mb-1 text-base font-semibold">{{ titulo }}</h3>
    <p class="mb-4 text-sm text-slate-500 dark:text-slate-400">{{ explicacao }}</p>

    <!-- Controle de Acesso: sem a key de edição, a aba é de consulta. -->
    <div v-if="somenteLeitura" class="p-3 mb-4 rounded-lg" style="background:rgba(148,163,184,.14)">
      <p class="text-sm">
        👁️ <b>Somente leitura.</b> Você pode conferir o fluxo desta caixa, mas não
        salvar alterações. Peça a liberação em <b>Controle de Acesso → Fluxo de
        atendimento no Webchat → Configurar/salvar o fluxo</b>.
      </p>
    </div>

    <!-- conflito com o bot da caixa -->
    <div v-if="agentBot" class="p-3 mb-4 rounded-lg" style="background:rgba(245,158,11,.12)">
      <p class="text-sm">
        ⚠️ Esta caixa usa hoje o bot <b>{{ agentBot.name }}</b> (aba "Configuração do Bot").
        É <b>um ou outro</b>: ao ligar a IA daqui, aquele bot é desligado desta caixa.
      </p>
    </div>

    <label class="flex gap-2 items-center mb-1 cursor-pointer">
      <input v-model="enabled" type="checkbox" style="margin:0" :disabled="somenteLeitura" />
      <span class="text-sm font-medium">Ativar a IA deste fluxo nesta caixa</span>
      <span class="px-2 py-0.5 text-xs font-semibold rounded-full"
            :style="enabled ? 'background:rgba(0,168,132,.18);color:#0f7c63'
                            : 'background:rgba(148,163,184,.2)'">
        {{ enabled ? 'IA do fluxo no comando' : 'bot da caixa no comando' }}
      </span>
    </label>
    <p class="mb-4 text-xs text-slate-500">
      Desligado, nada muda: a caixa segue como está hoje.
    </p>

    <div class="flex gap-2 mb-4">
      <button type="button" class="px-3 py-1 text-xs font-semibold rounded"
              :style="aba==='ia' ? 'background:#00a884;color:#fff' : 'border:1px solid rgba(148,163,184,.4)'"
              @click="aba='ia'">🤖 As IAs</button>
      <button type="button" class="px-3 py-1 text-xs font-semibold rounded"
              :style="aba==='fluxo' ? 'background:#00a884;color:#fff' : 'border:1px solid rgba(148,163,184,.4)'"
              @click="aba='fluxo'">🔀 Roteiro (botões e carrossel)</button>
    </div>

    <div class="p-4 mb-4 rounded-lg" style="background:rgba(148,163,184,.08)">
      <p class="mb-1 text-sm font-semibold">🔑 Modelo e chave desta caixa</p>
      <p class="mb-3 text-xs text-slate-500">
        Use a chave geral (tela <b>Credenciais e Chaves</b>) ou uma só desta
        caixa — assim dá para separar o custo por número e revogar uma sem
        derrubar as outras.
      </p>
      <div class="mb-3 flex flex-col gap-1">
        <label class="flex gap-2 items-center text-xs cursor-pointer">
          <input v-model="usarChaveGeral" type="radio" :value="true" style="margin:0" />
          <span>
            Usar a chave <b>geral</b> da conta
            <span v-if="openaiGeralDica" class="text-slate-500">({{ openaiGeralDica }})</span>
            <span v-else class="text-slate-500">— nenhuma cadastrada ainda</span>
          </span>
        </label>
        <label class="flex gap-2 items-center text-xs cursor-pointer">
          <input v-model="usarChaveGeral" type="radio" :value="false" style="margin:0" />
          <span>Usar uma chave <b>própria desta caixa</b></span>
        </label>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
        <select v-model="iaModelo" style="max-width:190px">
          <option v-for="m in modelos" :key="m" :value="m">{{ m }}</option>
        </select>
        <input v-model="iaChave" type="password" autocomplete="new-password"
               :disabled="usarChaveGeral"
               :placeholder="usarChaveGeral
                 ? 'usando a chave geral da conta'
                 : (iaTemChave ? `Chave salva (${iaDica}) — digite para trocar` : 'Chave da OpenAI (sk-…)')"
               style="flex:1;min-width:240px" />
        <button v-if="!somenteLeitura" type="button" class="text-xs px-3 py-1 rounded"
                style="background:#00a884;color:#fff" :disabled="iaSalvando"
                @click="salvarIa">{{ iaSalvando ? 'Salvando…' : 'Salvar chave' }}</button>
      </div>
      <p class="mt-2 text-xs text-slate-500">
        A chave <b>nunca volta para a tela</b> — só o final dela, para você
        reconhecer qual está gravada.
        <b v-if="chaveOrigem === 'nenhuma'" style="color:#f15c6d">
          Nenhuma chave disponível: as IAs não vão responder.
        </b>
        <span v-else-if="chaveOrigem">
          Em uso agora: chave {{ chaveOrigem === 'geral' ? 'geral da conta' : 'desta caixa' }}.
        </span>
      </p>
    </div>

    <!-- ========================= IAs ========================= -->
    <div v-if="aba==='ia'">
      <div v-if="loading" class="text-sm text-slate-500">Carregando…</div>

      <div v-else class="flex gap-4" style="align-items:flex-start">
        <!-- lista -->
        <div style="flex:0 0 220px">
          <p class="mb-2 text-xs font-semibold text-slate-500">
            SUAS IAs ({{ agents.length }} de {{ 8 }})
          </p>
          <div v-for="a in agents" :key="a.id"
               class="p-2 mb-2 rounded-lg cursor-pointer"
               :style="selId===a.id ? 'border:2px solid #00a884' : 'border:1px solid rgba(148,163,184,.35)'"
               @click="selId=a.id">
            <div class="flex gap-1 items-center text-sm font-semibold">
              <span>{{ a.ativo ? '🟢' : '⚪' }}</span>
              <span>{{ a.nome || a.id }}</span>
            </div>
            <div class="text-xs text-slate-500">
              <span v-if="defaultAgent===a.id">atende primeiro · </span>
              {{ (a.tools||[]).length }} ferramenta(s)
            </div>
          </div>
          <button type="button" class="wnova mt-3" :disabled="agents.length>=8"
                  @click="addAgent()">＋ Nova IA</button>
          <p class="mt-2 text-[11px] text-slate-500">
            Você escolhe o nome e o que ela faz. Se quiser um ponto de partida,
            crie a IA e use um exemplo dentro do editor.
          </p>
        </div>

        <!-- editor da IA -->
        <div v-if="sel" style="flex:1;min-width:0">
          <div class="flex gap-3 items-center mb-3">
            <input ref="nomeIa" v-model="sel.nome" type="text" maxlength="40"
                   placeholder="Nome da IA (ex.: Ana — recepção, SOMA, FACTA)"
                   style="max-width:300px" />
            <label class="flex gap-1 items-center text-xs cursor-pointer">
              <input v-model="sel.ativo" type="checkbox" style="margin:0" /> ligada
            </label>
            <label class="flex gap-1 items-center text-xs cursor-pointer">
              <input type="radio" :checked="defaultAgent===sel.id" style="margin:0"
                     @change="defaultAgent=sel.id" /> atende primeiro
            </label>
            <button type="button" class="ml-auto text-xs" style="color:#f15c6d"
                    @click="removeAgent(sel.id)">excluir</button>
          </div>

          <div class="flex flex-wrap gap-2 items-center mb-1">
            <label class="text-xs font-medium">Como ela conversa (personalidade)</label>
            <span class="text-[11px] text-slate-500">preencher com um exemplo:</span>
            <button type="button" class="wex" @click="usarExemplo('recepcao')">recepção</button>
            <button type="button" class="wex" @click="usarExemplo('banco')">especialista</button>
            <button type="button" class="wex" @click="usarExemplo('duvidas')">tira-dúvidas</button>
          </div>
          <textarea v-model="sel.persona" rows="4" maxlength="4000" class="mb-1"
                    placeholder="Escreva com suas palavras quem é essa IA e como ela fala com o cliente." />
          <p class="mb-3 text-xs text-slate-500">{{ (sel.persona||'').length }} / 4000</p>

          <label class="block mb-1 text-xs font-medium">O que ela DEVE fazer</label>
          <div v-for="(r,i) in sel.regras" :key="'r'+i" class="flex gap-2 mb-1">
            <input v-model="sel.regras[i]" type="text" maxlength="300" placeholder="Uma regra por linha" />
            <button type="button" class="text-xs" style="color:#f15c6d" @click="sel.regras.splice(i,1)">×</button>
          </div>
          <button type="button" class="mb-3 text-xs" style="color:#00a884" @click="addLinha(sel.regras)">＋ regra</button>

          <label class="block mb-1 text-xs font-medium">O que ela NUNCA pode fazer</label>
          <div v-for="(p,i) in sel.proibicoes" :key="'p'+i" class="flex gap-2 mb-1">
            <input v-model="sel.proibicoes[i]" type="text" maxlength="200" placeholder="Uma proibição por linha" />
            <button type="button" class="text-xs" style="color:#f15c6d" @click="sel.proibicoes.splice(i,1)">×</button>
          </div>
          <button type="button" class="mb-3 text-xs" style="color:#00a884" @click="addLinha(sel.proibicoes)">＋ proibição</button>

          <div class="p-3 mb-3 rounded-lg" style="background:rgba(148,163,184,.08)">
            <p class="mb-2 text-xs font-semibold">🛡️ Segurança</p>
            <label class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
              <input v-model="sel.guardrail_opcoes.exigir_simulacao_real_para_oferta" type="checkbox" style="margin:0" />
              <span><b>Só falar valores que vieram de uma consulta real</b> — impede a IA de inventar oferta</span>
            </label>
            <label class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
              <input v-model="sel.guardrail_opcoes.bloquear_promessa_aprovacao" type="checkbox" style="margin:0" />
              <span>Nunca prometer aprovação, liberação ou prazo</span>
            </label>
            <label class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
              <input v-model="sel.guardrail_opcoes.bloquear_pii_na_saida" type="checkbox" style="margin:0" />
              <span>Esconder CPF e documentos nas respostas</span>
            </label>
            <label class="flex gap-2 items-center mb-2 text-xs cursor-pointer">
              <input v-model="sel.guardrail_opcoes.somente_nosso_produto" type="checkbox" style="margin:0" />
              <span>Falar somente do nosso produto</span>
            </label>
            <label class="block mb-1 text-xs font-medium">Frases que ela nunca pode escrever</label>
            <div v-for="(f,i) in sel.frases_proibidas" :key="'f'+i" class="flex gap-2 mb-1">
              <input v-model="sel.frases_proibidas[i]" type="text" maxlength="80" placeholder="Ex.: aprovação garantida" />
              <button type="button" class="text-xs" style="color:#f15c6d" @click="sel.frases_proibidas.splice(i,1)">×</button>
            </div>
            <button type="button" class="mb-2 text-xs" style="color:#00a884" @click="addLinha(sel.frases_proibidas)">＋ frase</button>
            <label class="block mb-1 text-xs font-medium">Outras regras de segurança (texto livre)</label>
            <textarea v-model="sel.guardrail" rows="2" maxlength="2000"
                      placeholder="Escreva com suas palavras o que ela precisa respeitar." />
          </div>

          <label class="block mb-1 text-xs font-medium">Perguntas frequentes</label>
          <div v-for="(f,i) in sel.faq" :key="'q'+i" class="p-2 mb-2 rounded" style="background:rgba(148,163,184,.08)">
            <div class="flex gap-2 mb-1">
              <input v-model="f.pergunta" type="text" maxlength="200" placeholder="Pergunta do cliente" />
              <button type="button" class="text-xs" style="color:#f15c6d" @click="sel.faq.splice(i,1)">×</button>
            </div>
            <textarea v-model="f.resposta" rows="2" maxlength="800" placeholder="Resposta exata que ela deve dar" />
          </div>
          <button type="button" class="mb-3 text-xs" style="color:#00a884" @click="addFaq(sel)">＋ pergunta</button>

          <label class="block mb-1 text-xs font-medium">Ferramentas que ela pode usar</label>
          <p v-if="!tools.length" class="mb-2 text-xs text-slate-500">
            Nenhuma ferramenta cadastrada ainda — cadastre abaixo.
          </p>
          <label v-for="t in tools" :key="t.nome" class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
            <input type="checkbox" style="margin:0" :checked="sel.tools.includes(t.nome)"
                   @change="toggleTool(sel, t.nome)" />
            <span><b>{{ t.titulo || t.nome }}</b> — {{ t.descricao || 'sem explicação' }}</span>
          </label>

          <label class="block mt-3 mb-1 text-xs font-medium">Pode transferir para</label>
          <label v-for="o in agents.filter(x => x.id !== sel.id)" :key="'t'+o.id"
                 class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
            <input type="checkbox" style="margin:0" :checked="sel.pode_transferir_para.includes(o.id)"
                   @change="toggleTransfer(sel, o.id)" />
            <span>{{ o.nome || o.id }}</span>
          </label>
          <label class="flex gap-2 items-center mb-1 text-xs cursor-pointer">
            <input type="checkbox" style="margin:0" :checked="sel.pode_transferir_para.includes('humano')"
                   @change="toggleTransfer(sel, 'humano')" />
            <span>Atendente humano</span>
          </label>
        </div>
        <p v-else class="text-sm text-slate-500">
          Nenhuma IA ainda. Clique em <b>＋ Nova IA</b> e dê o nome que quiser —
          uma recepcionista, uma por banco, como preferir.
        </p>
      </div>

      <!-- ferramentas da caixa -->
      <div class="pt-4 mt-6" style="border-top:1px solid rgba(148,163,184,.25)">
        <p class="mb-1 text-sm font-semibold">🔧 Ferramentas desta caixa</p>
        <p class="mb-3 text-xs text-slate-500">
          Uma ferramenta é um endereço que a IA chama para buscar ou gravar algo
          (consultar saldo, simular, abrir proposta). Só <b>https</b>, e endereços
          de rede interna são recusados por segurança.
        </p>
        <div v-for="(t,i) in tools" :key="'tool'+i" class="p-3 mb-2 rounded-lg" style="background:rgba(148,163,184,.08)">
          <div class="flex gap-2 items-center">
            <b class="text-xs">{{ t.titulo || t.nome }}</b>
            <button type="button" class="text-xs" style="color:#00a884"
                    @click="toolAberta = toolAberta===t.nome ? null : t.nome">
              {{ toolAberta===t.nome ? 'fechar' : 'editar' }}
            </button>
            <button type="button" class="ml-auto text-xs" style="color:#f15c6d" @click="tools.splice(i,1)">excluir</button>
          </div>
          <div v-if="toolAberta===t.nome" class="mt-2">
            <input v-model="t.titulo" type="text" maxlength="60" placeholder="Nome amigável — ex.: Consultar saldo FGTS" class="mb-1" />
            <input v-model="t.nome" type="text" maxlength="48" placeholder="identificador (só letras minúsculas e _)" class="mb-1" />
            <textarea v-model="t.descricao" rows="2" maxlength="500" class="mb-1"
                      placeholder="O que ela faz — explique como se fosse para um atendente novo." />
            <input v-model="t.quando_usar" type="text" maxlength="300" class="mb-1"
                   placeholder="Quando a IA deve usar — ex.: assim que o cliente informar o CPF" />
            <input v-model="t.url" type="text" maxlength="400" class="mb-1" placeholder="https://…" />
            <div class="flex gap-2 mb-2">
              <select v-model="t.metodo" style="max-width:110px">
                <option value="POST">POST</option><option value="GET">GET</option>
              </select>
              <input v-model.number="t.timeout_seg" type="number" min="3" max="30" style="max-width:110px"
                     placeholder="timeout" />
              <input v-model="t.mensagem_espera" type="text" maxlength="120"
                     placeholder="Mensagem enquanto espera — ex.: Só um instante…" />
            </div>
            <p class="mb-1 text-xs font-medium">O que a IA precisa informar</p>
            <div v-for="(p,pi) in t.parametros" :key="'p'+pi" class="flex gap-2 mb-1">
              <input v-model="p.nome" type="text" maxlength="40" placeholder="cpf" style="max-width:120px" />
              <select v-model="p.tipo" style="max-width:130px">
                <option v-for="tp in tipos" :key="tp.v" :value="tp.v">{{ tp.t }}</option>
              </select>
              <input v-model="p.descricao" type="text" maxlength="200" placeholder="explicação para a IA" />
              <label class="flex gap-1 items-center text-xs">
                <input v-model="p.obrigatorio" type="checkbox" style="margin:0" /> obrig.
              </label>
              <button type="button" class="text-xs" style="color:#f15c6d" @click="t.parametros.splice(pi,1)">×</button>
            </div>
            <button type="button" class="text-xs" style="color:#00a884" @click="addParam(t)">＋ informação</button>
          </div>
        </div>
        <button type="button" class="text-xs" style="color:#00a884" :disabled="tools.length>=30"
                @click="addTool">＋ cadastrar ferramenta</button>
      </div>
    </div>

    <!-- ========================= roteiro ========================= -->
    <div v-show="aba==='fluxo'">
      <!-- Segredos e allowlist dos nós de ação (HTTP / Supabase / Script) -->
      <div class="p-3 mb-4 rounded-lg" style="background:rgba(148,163,184,.08)">
        <p class="mb-1 text-sm font-semibold">🔑 Segredos & Supabase</p>
        <p class="mb-2 text-xs" style="color:#64748b">
          Segredos ficam fora do roteiro; use <code>&#123;&#123;secret:NOME&#125;&#125;</code> no HTTP/Script.
          Só as tabelas liberadas aqui aparecem no bloco Supabase.
        </p>

        <p class="mt-2 mb-1 text-xs font-medium">Segredos</p>
        <div v-for="(s,i) in segredos" :key="'s'+i" class="flex gap-2 mb-1">
          <input v-model="s.nome" type="text" placeholder="token" style="max-width:160px" />
          <input v-model="s.valor" type="password"
                 :placeholder="s.dica ? ('•••••• ('+s.dica+') — manter') : 'valor'" class="flex-1" />
          <button type="button" class="wx" @click="delSegredo(i)">✕</button>
        </div>
        <button type="button" class="wadd mb-3" @click="addSegredo">＋ segredo</button>

        <p class="mt-2 mb-1 text-xs font-medium">Tabelas Supabase liberadas</p>
        <div class="flex flex-wrap gap-2 mb-2">
          <span v-for="(t,i) in supabaseTabelas" :key="'t'+i" class="wtag">
            {{ t }} <button type="button" @click="delTabela(i)">✕</button>
          </span>
          <span v-if="!supabaseTabelas.length" class="text-xs" style="color:#94a3b8">nenhuma liberada</span>
        </div>
        <div class="flex gap-2">
          <input v-model="tabelaNova" type="text" placeholder="nome_da_tabela"
                 class="flex-1" @keyup.enter="addTabela" />
          <button type="button" class="wadd" @click="addTabela">＋ liberar</button>
        </div>
      </div>

      <!-- E1: o botão de salvar do canvas (dentro da toolbar, acessível mesmo em
           tela cheia) dispara o MESMO save() do pai, que já faz o
           POST /mazam_web/ia com o roteiro (this.$refs.flow.exportFlow()). -->
      <FlowConfigurationPage ref="flow" :inbox="inbox" :channel="canal"
                             :embedded="true" :agentes-ia="agents"
                             :somente-leitura="somenteLeitura" @save="save" />
    </div>

    <div v-if="problemas.length" class="p-3 mt-4 rounded-lg" style="background:rgba(241,92,109,.10)">
      <p class="mb-1 text-xs font-semibold" style="color:#f15c6d">Revise antes de ativar:</p>
      <ul class="text-xs" style="list-style:disc;padding-left:18px">
        <li v-for="(p,i) in problemas" :key="i">{{ p }}</li>
      </ul>
    </div>

    <div v-if="!somenteLeitura" class="mt-5">
      <button type="button" class="fsalvar" :disabled="saving || loading" @click="save">
        {{ saving ? 'Salvando…' : 'Salvar configuração' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* O `class="button"` do Chatwoot não se aplica fora dos formulários dele —
   o botão saía como texto solto. Estilo próprio, prefixado. */
.fsalvar {
  background: #00a884;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}
.fsalvar:hover:not(:disabled) { background: #01916f; }
.fsalvar:disabled { opacity: .55; cursor: default; }
.wx {
  width: 26px; flex: 0 0 26px; border-radius: 6px; border: 1px solid #e2426022;
  background: rgba(241,92,109,.12); color: #f15c6d; font-size: 12px;
}
.wadd {
  font-size: 12px; color: #00a884; background: rgba(0,168,132,.10);
  border: 1px dashed #00a88455; border-radius: 6px; padding: 4px 10px; display: inline-block;
}
.wtag {
  display: inline-flex; align-items: center; gap: 6px; font-size: 12px;
  background: rgba(0,168,132,.12); color: #01916f; border-radius: 6px; padding: 3px 8px;
}
.wtag button { color: #f15c6d; font-size: 11px; }
.wnova {
  display: block; width: 100%; padding: 8px 10px; border-radius: 8px;
  background: #00a884; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer;
}
.wnova:hover:not(:disabled) { background: #01916f; }
.wnova:disabled { opacity: .5; cursor: default; }
.wex {
  font-size: 11px; padding: 2px 8px; border-radius: 999px; cursor: pointer;
  background: rgba(148,163,184,.16); color: #64748b;
}
.wex:hover { background: rgba(0,168,132,.16); color: #00a884; }
</style>

